import { useCallback, useEffect, useState } from 'react';
import { View, Alert, ActivityIndicator } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '@/theme';
import { Button } from '@/components/ui/Button';
import { Caption } from '@/components/ui/Text';
import {
  cacheTripPackForOffline,
  getCachedTripData,
  getLastSyncTime,
  removeCachedTrip,
  setTripPackAutoCacheDisabled,
} from '@/services/offlineTrips';
import { networkMonitor } from '@/services/networkMonitor';
import { logger } from '@/services/logger';
import type { Trip } from '@/services/trips';
import type { Booking } from '@/services/bookings';
import type { ItineraryItem } from '@/services/itinerary';
import { OfflineAccessCardShell } from '@/components/offline/OfflineAccessCardShell';

export type ItineraryOfflineCardProps = {
  trip: Trip;
  itineraryItems: ItineraryItem[];
  bookings: Booking[];
  isPremium: boolean;
  onOpenOfflineUpgrade?: () => void;
  onCacheChanged?: () => void;
};

export function ItineraryOfflineCard({
  trip,
  itineraryItems,
  bookings,
  isPremium,
  onOpenOfflineUpgrade,
  onCacheChanged,
}: ItineraryOfflineCardProps) {
  const theme = useTheme();
  const [isDownloaded, setIsDownloaded] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const [lastSynced, setLastSynced] = useState<number | null>(null);

  const refreshStatus = useCallback(async () => {
    try {
      const cached = await getCachedTripData(trip.id);
      setIsDownloaded(!!cached);
      const fromPack =
        cached && typeof cached.lastSynced === 'number' && cached.lastSynced > 0
          ? cached.lastSynced
          : null;
      const globalSync = await getLastSyncTime();
      setLastSynced(fromPack ?? globalSync);
    } catch (error) {
      logger.error('[ItineraryOfflineCard] Error checking status', error instanceof Error ? error : new Error(String(error)));
    }
  }, [onCacheChanged, trip.id]);

  useEffect(() => {
    void refreshStatus();
    return networkMonitor.addListener(() => {
      void refreshStatus();
    });
  }, [trip.id, refreshStatus]);

  const handleDownload = async () => {
    if (!isPremium) {
      onOpenOfflineUpgrade?.();
      return;
    }
    if (!networkMonitor.isOnline()) {
      Alert.alert('Offline', 'You need to be online to download itinerary data.');
      return;
    }

    setIsDownloading(true);
    try {
      await setTripPackAutoCacheDisabled(trip.id, false);
      const cached = await cacheTripPackForOffline(trip, {
        bookings,
        itinerary: itineraryItems,
      });
      if (!cached) {
        throw new Error('Trip pack cache write was skipped or failed');
      }
      await refreshStatus();
      onCacheChanged?.();
      Alert.alert('Success', 'Itinerary data downloaded for offline access.');
    } catch (error) {
      logger.error('[ItineraryOfflineCard] Error downloading', error instanceof Error ? error : new Error(String(error)));
      Alert.alert('Error', 'Failed to download itinerary data. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  };

  const handleRemove = () => {
    Alert.alert(
      'Remove from device?',
      'Offline copies of this trip’s plan, bookings, and itinerary will be deleted from this device. Nothing is removed from the cloud.',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Remove',
          style: 'destructive',
          onPress: () => {
            void (async () => {
              setIsRemoving(true);
              try {
                await removeCachedTrip(trip.id);
                await refreshStatus();
                onCacheChanged?.();
              } catch (error) {
                logger.error(
                  '[ItineraryOfflineCard] Error removing cache',
                  error instanceof Error ? error : new Error(String(error)),
                );
                Alert.alert('Error', 'Could not remove offline data.');
              } finally {
                setIsRemoving(false);
              }
            })();
          },
        },
      ],
    );
  };

  const syncLabel = lastSynced
    ? `Last synced ${new Date(lastSynced).toLocaleDateString()}`
    : 'Itinerary available offline';

  if (!isPremium && !isDownloaded) {
    return (
      <OfflineAccessCardShell
        icon="cloud-done-outline"
        active={false}
        title="Download for Offline"
        subtitle="Offline trip download is a Premium feature. Upgrade to save your itinerary, bookings, and activities for offline access."
        trailing={
          <Button
            label="Upgrade"
            variant="secondary"
            onPress={() => onOpenOfflineUpgrade?.()}
            icon={<Ionicons name="sparkles-outline" size={18} color={theme.colors.text} />}
          />
        }
      />
    );
  }

  // Non-premium with a local pack (e.g. legacy auto-cache or downgrade): do not show "Available Offline" / Ready.
  if (!isPremium && isDownloaded) {
    return (
      <OfflineAccessCardShell
        icon="cloud-done-outline"
        active={false}
        title="Local trip copy"
        subtitle="Offline access is a Premium feature. Upgrade to use it, or remove this browser copy."
        trailing={
          <>
            <Button
              label="Upgrade"
              variant="secondary"
              onPress={() => onOpenOfflineUpgrade?.()}
              icon={<Ionicons name="sparkles-outline" size={18} color={theme.colors.text} />}
            />
            <Button
              label={isRemoving ? 'Removing…' : 'Remove'}
              variant="secondary"
              onPress={handleRemove}
              disabled={isRemoving || isDownloading}
              icon={
                isRemoving ? (
                  <ActivityIndicator size="small" color={theme.colors.error || '#FF3B30'} />
                ) : (
                  <Ionicons name="trash-outline" size={18} color={theme.colors.error || '#FF3B30'} />
                )
              }
            />
          </>
        }
      />
    );
  }

  return (
    <OfflineAccessCardShell
      icon="cloud-done-outline"
      active={isDownloaded}
      title={isDownloaded ? 'Available Offline' : 'Download for Offline'}
      subtitle={isDownloaded ? syncLabel : 'Download itinerary, bookings, and activities for offline access'}
      trailing={
        !isDownloaded ? (
          <Button
            label={isDownloading ? 'Downloading...' : 'Download'}
            variant="secondary"
            onPress={handleDownload}
            disabled={isDownloading}
            icon={
              isDownloading ? (
                <ActivityIndicator size="small" color={theme.colors.text} />
              ) : (
                <Ionicons name="download-outline" size={18} color={theme.colors.text} />
              )
            }
          />
        ) : (
          <>
            <View
              style={{
                paddingHorizontal: theme.spacing(1.5),
                paddingVertical: theme.spacing(0.5),
                borderRadius: theme.radii.pill,
                backgroundColor: theme.colors.success + '22',
                flexDirection: 'row',
                alignItems: 'center',
                gap: theme.spacing(0.5),
                borderWidth: 1,
                borderColor: (theme.colors.success || '#34C759') + '44',
              }}
            >
              <Ionicons name="checkmark-circle" size={16} color={theme.colors.success || '#34C759'} />
              <Caption style={{ color: theme.colors.success || '#34C759', fontSize: 11, fontWeight: '600' }}>
                Ready
              </Caption>
            </View>
            <Button
              label={isRemoving ? 'Removing…' : 'Remove'}
              variant="secondary"
              onPress={handleRemove}
              disabled={isRemoving || isDownloading}
              icon={
                isRemoving ? (
                  <ActivityIndicator size="small" color={theme.colors.error || '#FF3B30'} />
                ) : (
                  <Ionicons name="trash-outline" size={18} color={theme.colors.error || '#FF3B30'} />
                )
              }
            />
          </>
        )
      }
    />
  );
}
